import type { DHApp } from '~/shared/types';
import { recordSuggestion } from '../storage/recordSuggestion';
import { getAllRecordsUntilTime } from '../storage/getAllRecordsUntilTime';
import type { AppSuggestionOptions } from '../../types';

export type GetNextAppToSuggestArgs = {
  /**
   * The app that was previously shown (to record)
   */
  previousApp?: DHApp | null;
  /**
   * Array of enabled and visible apps
   */
  apps: Array<DHApp>;
  /**
   * Configuration options for suggestion logic
   */
  options?: AppSuggestionOptions;
};

/**
 * Get the next app to suggest to the user based on:
 * - Apps that haven't been shown yet (ranked by weight)
 * - If all apps have been shown, show the one shown longest ago
 * Uses records up to 15 minutes in the past as reference point
 *
 * @returns The app to suggest, or null if no suitable app found
 */
function sortByPinnedAndWeight(apps: Array<DHApp>): Array<DHApp> {
  return [...apps].sort((a, b) => {
    const aIsPinned = !!a.pinned?.enabled;
    const bIsPinned = !!b.pinned?.enabled;

    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    let aWeight: number;
    let bWeight: number;
    if (aIsPinned && bIsPinned) {
      aWeight = a.pinned!.weight;
      bWeight = b.pinned!.weight;
    } else {
      aWeight = a.weight;
      bWeight = b.weight;
    }

    if (aWeight !== bWeight) return bWeight - aWeight;
    return Math.random() - 0.5;
  });
}

export async function getNextAppToSuggest({
  previousApp,
  apps,
  options = {},
}: GetNextAppToSuggestArgs): Promise<DHApp | null> {
  const { context = {}, contextAware = true } = options;

  // Filter out hidden apps
  const nonHiddenApps = apps.filter((app) => app.hidden !== true);

  if (nonHiddenApps.length === 0) {
    return null;
  }

  const highestWeight = Math.max(...nonHiddenApps.map((app) => app.weight));
  const availableApps = nonHiddenApps.filter(
    (app) => app.weight === highestWeight || !!app.pinned?.enabled,
  );

  // When not context-aware: always show highest pinned or highest weight, no rotation
  if (!contextAware) {
    return sortByPinnedAndWeight(availableApps)[0] ?? null;
  }

  // Record the previous app if it exists
  if (previousApp && context.accountId) {
    await recordSuggestion({
      slug: previousApp.slug,
      type: 'Shown',
      accountId: context.accountId,
    });
  }

  // Get all "Shown" records from the last 15 minutes
  const recentShown = await getAllRecordsUntilTime({
    accountId: context.accountId,
    minutesInPast: 15,
  });

  // Filter only "Shown" type and get latest per slug
  const shownBySlug = new Map<string, { slug: string; timestamp: string }>();
  for (const record of recentShown) {
    if (record.type === 'Shown') {
      const existing = shownBySlug.get(record.slug);
      if (!existing || record.timestamp > existing.timestamp) {
        shownBySlug.set(record.slug, {
          slug: record.slug,
          timestamp: record.timestamp,
        });
      }
    }
  }

  const shownSlugs = new Set(shownBySlug.keys());

  // Separate apps into: not shown yet vs already shown
  const notShownApps = availableApps.filter((app) => !shownSlugs.has(app.slug));
  const alreadyShownApps = availableApps.filter((app) =>
    shownSlugs.has(app.slug),
  );

  // If there are apps that haven't been shown yet
  if (notShownApps.length > 0) {
    return sortByPinnedAndWeight(notShownApps)[0];
  }

  // All apps have been shown - find the one shown longest ago
  if (alreadyShownApps.length > 0) {
    const sortedByOldest = [...alreadyShownApps].sort((a, b) => {
      const aTimestamp = shownBySlug.get(a.slug)?.timestamp ?? '';
      const bTimestamp = shownBySlug.get(b.slug)?.timestamp ?? '';
      return aTimestamp.localeCompare(bTimestamp);
    });
    return sortedByOldest[0];
  }

  return availableApps[0];
}
